/**
 * Unlimited Entitlement Storage Utility
 * StoreKit on the native side is the source of truth - this is a cache so the
 * UI can render correctly before the bridge answers, and so the web build has
 * something to read at all.
 */
import { useState, useEffect } from 'react';
import { UNLIMITED_EVENT, requestEntitlement } from './bridge.js';

/**
 * Undos allowed per board attempt without the purchase. Lives here rather than
 * in Board because it defines the free tier, not the game.
 */
export const FREE_UNDOS = 3;

const Entitlements = {
  UNLIMITED_KEY: 'unlimited',

  /**
   * Whether the one-time Unlimited purchase has been made
   * @returns {boolean}
   */
  isUnlimited() {
    try {
      return localStorage.getItem(this.UNLIMITED_KEY) === 'true';
    } catch (error) {
      console.error('Error reading entitlement from localStorage:', error);
      return false;
    }
  },

  /**
   * Cache what native told us
   * @param {boolean} owned
   */
  setUnlimited(owned) {
    try {
      localStorage.setItem(this.UNLIMITED_KEY, String(owned === true));
    } catch (error) {
      console.error('Error writing entitlement to localStorage:', error);
    }
  },
};

/**
 * Read the entitlement in a component and re-render when native reports a
 * purchase, a restore, or a launch-time check.
 * @returns {boolean}
 */
export function useUnlimited() {
  const [unlimited, setUnlimitedState] = useState(() => Entitlements.isUnlimited());

  useEffect(() => {
    const onChange = (e) => {
      Entitlements.setUnlimited(e.detail);
      setUnlimitedState(e.detail);
    };
    window.addEventListener(UNLIMITED_EVENT, onChange);
    // The cache can be stale - the purchase may have been restored elsewhere
    requestEntitlement();
    return () => window.removeEventListener(UNLIMITED_EVENT, onChange);
  }, []);

  return unlimited;
}

export default Entitlements;
